import { useState, useEffect } from 'react'
import { createClient } from '@/utils/supabase/client'
import { User } from '@supabase/supabase-js'
import { supabaseCache, CACHE_KEYS } from '@/lib/cache'

export function useCachedUser() {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const supabase = createClient()

  useEffect(() => {
    let isMounted = true
    
    const fetchUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser()
        
        if (!isMounted) return
        
        if (error) {
          // Silently handle expected unauthenticated states
          if (error.message?.includes('refresh_token_not_found') ||
              error.message?.includes('Invalid Refresh Token') ||
              error.name === 'AuthSessionMissingError' ||
              error.message?.includes('Auth session missing')) {
            setUser(null)
            setIsLoggedIn(false)
          } else {
            console.log('Error fetching user:', error)
            setUser(null)
            setIsLoggedIn(false)
          }
        } else {
          setUser(user)
          setIsLoggedIn(!!user)
          
          // Cache user data if authenticated
          if (user) {
            supabaseCache.set(CACHE_KEYS.USER(user.id), user)
          }
        }
      } catch (error) {
        console.log('Error in useCachedUser hook:', error)
        if (isMounted) {
          setUser(null)
          setIsLoggedIn(false)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchUser()

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return
        
        if (event === 'SIGNED_IN') {
          const user = session?.user ?? null
          setUser(user)
          setIsLoggedIn(!!user)
          
          // Cache user data and invalidate related caches
          if (user) {
            supabaseCache.set(CACHE_KEYS.USER(user.id), user)
            // Invalidate workspace and profile caches since they depend on user
            supabaseCache.invalidate('workspaces:')
            supabaseCache.invalidate('profile:')
            supabaseCache.invalidate('current-org:')
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null)
          setIsLoggedIn(false)
          
          // Clear user-related caches
          supabaseCache.invalidate('user:')
          supabaseCache.invalidate('workspaces:')
          supabaseCache.invalidate('profile:')
          supabaseCache.invalidate('current-org:')
          supabaseCache.invalidate('runs:')
          supabaseCache.invalidate('cart:')
        }
      }
    )

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [supabase])

  return { user, isLoading, isLoggedIn }
}
